import { AppType } from "client/Api/Apps/common";
import {
	FormItem,
	Select,
	SelectItem,
	TextInput,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { EditAppSavedResult, useSaveApp } from "../useSaveApp";
import {
	IntegrationAppConnectionModel,
	getIntegrationAppConnections
} from "client/Api/Apps/EditApp/appParameters";
import {
	SaveIntegrationAppModel,
	EditIntegrationAppViewModel,
	saveIntegrationApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditIntegrationAppProps = {
	initialSettings: EditAppSettings<EditIntegrationAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditIntegrationApp = forwardRef(
	(
		props: EditIntegrationAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [connections, setConnections] = useState<readonly IntegrationAppConnectionModel[]>([]);

		const [formData, setFormData] = useState<SaveIntegrationAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveIntegrationAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return connections.map(
					(c: IntegrationAppConnectionModel): SelectItem<number> => ({
						value: c.id,
						text: c.name
					})
				);
			},
			[connections]
		);

		useEffect(
			() => {
				const cancellation = new AbortController();

				const loadIntegrationConnections = async (
					signal: AbortSignal
				): Promise<void> => {
					try {
						const connections: readonly IntegrationAppConnectionModel[] =
							await getIntegrationAppConnections(signal);

						setConnections(connections);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				};

				loadIntegrationConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[alertManager]
		);

		const prepareModelForSaving = useCallback(
			(model: SaveIntegrationAppModel): SaveIntegrationAppModel => {
				return {
					...model,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveIntegrationAppModel>(
			formHandler,
			saveIntegrationApp,
			AppType.Integration,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under(
				"connectionId",
				"navigationUrl",
				"destinationWindow"
			)
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem label={localize("_Connection")}>
					<Select
						{...formHandler.for("connectionId").attachToSelect()}
						options={connectionOptions}
						placeholder={localize("_SelectConnection")}
						menuTitle={localize("_Connection")}
					/>
					<ValidationError
						{...formHandler.for("connectionId").attachToValidationError()}
					/>
				</FormItem>

				<FormItem label={localize("_LiveTileNavigationUrl")}>
					<TextInput
						{...formHandler.for("navigationUrl").attachToTextInput()}
						placeholder={localize("_EnterNavigationUrl")}
					/>
					<ValidationError
						{...formHandler.for("navigationUrl").attachToValidationError()}
					/>
				</FormItem>

				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);
EditIntegrationApp.displayName = "EditIntegrationApp";

const validation = createValidationConfiguration<SaveIntegrationAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

validation.forScalar("navigationUrl")
	.add(
		isUrl({
			allowHttpsOnly: false
		})
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());

function convertToSaveModel(
	initialSettings: EditAppSettings<EditIntegrationAppViewModel>
): SaveIntegrationAppModel {
	const {
		connectionId,
		navigationUrl,
		destinationWindow,
		maintenanceWindow,
		conditionalAccessSettings
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		navigationUrl,
		destinationWindow,
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings)
	};
}
